import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  TextareaAutosize,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import CommonHeader from "../../CommonComponent/CommonHeader";
import CustomBreadcrumb from "../../CommonComponent/CommonBreadcrumb";
import CommonTabs from "../../CommonComponent/CommonTabs";
import {
  assignBreachUser,
  generateBreachPresignedUrl,
  getBreachData,
  updateBreachStatusChange,
  updateDataBreach,
} from "../../api/dataBreach";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import Loader from "../../CommonComponent/Loader";
import BreachStatusTimeline from "./BreachStatusTimeline";
import { getWebsiteById } from "../../api/websiteService";
import { getFormattedTimestamp } from "../../utils/dateUtils";
import GenerateEmail from "./GenerateEmail";
import ButtonCommon from "../../CommonComponent/ButtonCommon";
import CommonDrawer from "../../CommonComponent/CommonDrawer";
import { CloseOutlined, UploadFile } from "@mui/icons-material";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { getUserData } from "../../api/user";
import { fetchUserAttributes } from "aws-amplify/auth";

const DataBreachDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [breachDetails, setBreachDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState("1");
  const [timezone, setTimezone] = useState(null);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchUserData = async () => {
    try {
      const res = await getUserData();
      if (res?.status_code === 200) {
        setUserData(res?.data);
      }
    } catch (err) {
      throw err;
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const data = await fetchUserAttributes();
      setCurrentUser(data);
      if (data?.zoneinfo === "Billing_Admin") {
        await fetchUserData(); // Ensure it completes before moving on
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const getWebsiteDetails = async () => {
    try {
      const websiteId = localStorage.getItem("website_id");
      const websiteData = await getWebsiteById(websiteId);
      const timezoneFromApi = websiteData?.data?.time_zone;
      setTimezone(timezoneFromApi);
      return timezoneFromApi;
    } catch (err) {
      console.error("Error fetching website details:", err);
      return null;
    }
  };

  const fetchBreachDetails = async () => {
    try {
      const response = await getBreachData(id);
      setBreachDetails(response?.data || {});
    } catch (err) {
      showErrorToast("Failed to fetch breach details");
    }
  };

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        const timezoneFromApi = await getWebsiteDetails(); // fetch and set timezone

        await fetchCurrentUser(); // fetch current user

        // Fetch breach details only if id is present and timezone is loaded
        if (id && timezoneFromApi) {
          await fetchBreachDetails();
        }
      } catch (error) {
        console.error("Error loading data:", error);
        showErrorToast("Failed to load necessary data");
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
    // eslint-disable-next-line
  }, [id]); // rerun only if `id` changes

  const breadcrumbItems = [
    { text: "Data Breach", href: "/data-breach" },
    { text: "Data Breach Details" },
  ];

  const calculateDaysLapsed = (breachDetails, timezone) => {
    if (!breachDetails?.date_and_time_of_breach) {
      return "Not Available";
    }
    // Convert breach date to user's timezone
    const breachDateInTimeZone = dayjs
      .utc(breachDetails.date_and_time_of_breach)
      .tz(timezone)
      .startOf("day");

    let daysLapsed;

    // Check if breach is closed
    const isClosed = breachDetails.status?.closed?.closed_on;
    if (isClosed) {
      // Convert closed date
      const closedDateInTimeZone = dayjs
        .utc(breachDetails.status.closed.closed_on)
        .tz(timezone)
        .startOf("day");
      daysLapsed = closedDateInTimeZone.diff(breachDateInTimeZone, "days");
    } else {
      // Use current date if not closed
      const currentDate = dayjs().tz(timezone).startOf("day");
      daysLapsed = currentDate.diff(breachDateInTimeZone, "days");
    }
    return daysLapsed;
  };

  const breachInfoFields = [
    { key: "breach_id", label: "Breach ID" },
    {
      key: "days_lapsed",
      label: "Days Lapsed",
      format: (val, row) => calculateDaysLapsed(row, timezone),
    },
    { key: "assignee_name", label: "Assignee" },
    { key: "summary", label: "Summary" },
    { key: "detailed_description", label: "Detailed Description" },
    {
      key: "date_and_time_of_breach",
      label: "Date and Time of Breach",
      format: (val) =>
        val ? getFormattedTimestamp(val, timezone) : "Not Available",
    },
    { key: "location_of_breach", label: "Location of Breach" },
    { key: "impact", label: "Impact" },
    { key: "mitigations", label: "Mitigations" },
    { key: "safety_measures_for_dp", label: "Safety Measures" },
    { key: "root_cause", label: "Root Cause" },
    { key: "responsible_person", label: "Responsible Person" },
    { key: "remedial_measures", label: "Remedial Measures" },
    { key: "intimations_to_dps", label: "Intimations to DPs" },
  ];

  const tabs = [
    {
      label: "Data Breach Timeline",
      value: "1",
      content: breachDetails?.status ? (
        <BreachStatusTimeline
          status={breachDetails?.status}
          time_zone={timezone}
          attachment={breachDetails?.attachment}
        />
      ) : (
        <Typography>No timeline available</Typography>
      ),
    },
    {
      label: "Email for DPs / DPB",
      value: "2",
      content: <GenerateEmail time_zone={timezone} />,
    },
  ];

  const [formData, setFormData] = useState({
    summary: "",
    detailed_description: "",
    location_of_breach: "",
    impact: "",
    mitigations: "",
    safety_measures_for_dp: "",
    root_cause: "",
    responsible_person: "",
    remedial_measures: "",
    intimations_to_dps: "",
  });

  // Populate form with existing breach details
  useEffect(() => {
    if (breachDetails) {
      setFormData({
        summary: breachDetails.summary || "",
        detailed_description: breachDetails.detailed_description || "",
        location_of_breach: breachDetails.location_of_breach || "",
        attachment: breachDetails.attachment || "",
        impact: breachDetails.impact || "",
        mitigations: breachDetails.mitigations || "",
        safety_measures_for_dp: breachDetails.safety_measures_for_dp || "",
        root_cause: breachDetails.root_cause || "",
        responsible_person: breachDetails.responsible_person || "",
        remedial_measures: breachDetails.remedial_measures || "",
        intimations_to_dps: breachDetails.intimations_to_dps || "",
      });
    }
  }, [breachDetails]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });

    // Remove error message when user starts typing
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const [fileError, setFileError] = useState("");

  const handleCreateFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    const fileSizeLimit = 10 * 1024 * 1024; // 10MB in bytes
    if (selectedFile.name.includes(" ")) {
      setFileError("File name should not contain spaces.");
      return;
    }
    if (selectedFile.size > fileSizeLimit) {
      setFileError("File size should not exceed 10MB.");
      return;
    }
    setFileError(""); // Clear error if valid
    setFormData({ ...formData, attachment: selectedFile });
  };

  // Handle Input Changes
  const validateFields = () => {
    let newErrors = {};

    Object.keys(formData).forEach((key) => {
      if (key === "attachment") return; // Skip validation for attachment field
      const value = formData[key];
      if (typeof value === "string" && !value.trim()) {
        newErrors[key] = `${key.replaceAll("_", " ")} is required`;
      } else if (value == null) {
        newErrors[key] = `${key.replaceAll("_", " ")} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Update API Call
  const handleUpdate = async () => {
    if (!validateFields()) {
      return;
    }
    try {
      setLoading(true);
      let updatedFormData = { ...formData }; // Clone formData
      if (formData.attachment instanceof File) {
        const { presigned_url, file_name } = await generateBreachPresignedUrl(
          formData.attachment.name
        );

        if (!presigned_url) {
          throw new Error("Failed to get presigned URL");
        }
        const uploadResponse = await fetch(presigned_url, {
          method: "PUT",
          body: formData.attachment,
          headers: {
            "Content-Type": formData.attachment.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error("File upload failed!");
        }
        updatedFormData.attachment = file_name;
      }
      const res = await updateDataBreach(id, updatedFormData);
      if (res?.status_code === 200) {
        const response = await getBreachData(id);
        setBreachDetails(response?.data || {});
        showSuccessToast(res?.message || "Breach details updated successfully");
        setOpen(false); // Close modal
      }
    } catch (error) {
      showErrorToast(
        error?.response?.data?.message || "Error updating breach details"
      );
    } finally {
      setLoading(false);
    }
  };

  const [initialFormData, setInitialFormData] = useState({});

  useEffect(() => {
    if (open) {
      setInitialFormData(formData); // Store initial state when opening
    }
    //eslint-disable-next-line
  }, [open]);

  const isFormChanged =
    JSON.stringify(formData) !== JSON.stringify(initialFormData);

  const latestStatusKey = breachDetails?.status
    ? Object.keys(breachDetails.status || {}).pop() // Get the last status key
    : "Unknown";

  const [openModal, setOpenModal] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState(
    breachDetails?.status || ""
  );
  const [closedDate, setClosedDate] = useState(null);
  const [closedBy, setClosedBy] = useState("");
  const [comment, setComment] = useState("");
  const [initialFile, setInitialFile] = useState(null);
  const [finalFile, setFinalFile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState([]);
  const [reAssignModalOpen, setReAssignModalOpen] = useState(false);
  const [assignee, setAssignee] = useState("");

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    backgroundColor: "#F9FAFB",
    borderRadius: "16px",
    boxShadow: 24,
    p: 3,
    height: updatedStatus === "closed" ? "750px" : "auto",
    overflow: "auto",
  };

  const minDate = dayjs(breachDetails?.status?.new?.created_on).startOf("day");
  const maxDate = dayjs().endOf("day");

  const handleCloseModal = () => {
    setOpenModal(false);
    setUpdatedStatus("");
  };

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    setUpdatedStatus(newStatus);

    // Reset dependent fields if status is not "closed"
    if (newStatus !== "closed") {
      setClosedDate(null);
      setClosedBy("");
    }
  };

  const handleFileChange = (e, type) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    const fileSizeLimit = 10 * 1024 * 1024; // 10MB in bytes
    const validTypes = ["application/pdf", "image/jpeg", "image/jpg"];
    if (selectedFile.name.includes(" ")) {
      setFileError("File name should not contain spaces.");
      return;
    }
    if (selectedFile.size > fileSizeLimit) {
      setFileError("File size should not exceed 10MB.");
      return;
    }
    if (!validTypes.includes(selectedFile.type)) {
      setFileError(
        "Invalid file type! Only .pdf, .jpg, and .jpeg are allowed."
      );
      return;
    }
    setFileError(""); // Clear error if valid
    if (type === "initial") {
      setInitialFile(selectedFile);
    } else if (type === "final") {
      setFinalFile(selectedFile);
    }
  };

  const validateStatusChangeFields = () => {
    let tempErrors = {};

    if (!breachDetails?.breach_id)
      tempErrors.breach_id = "Breach ID is required.";
    if (!updatedStatus) tempErrors.status = "Status is required.";
    if (updatedStatus === "closed" && !closedDate)
      tempErrors.closedDate = "Closed Date is required.";
    if (updatedStatus === "closed" && !closedBy)
      tempErrors.closedBy = "Closed By is required.";
    if (updatedStatus === "closed" && !initialFile && !finalFile)
      tempErrors.file = "Attachment is required.";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStatusChangeFields()) return;
    setLoading(true);

    try {
      let initialFileName = null;
      let finalFileName = null;
      const presignedUrls = await Promise.all([
        initialFile ? generateBreachPresignedUrl(initialFile.name) : null,
        finalFile ? generateBreachPresignedUrl(finalFile.name) : null,
      ]);
      const [initialResponse, finalResponse] = presignedUrls;
      if (initialResponse) initialFileName = initialResponse.file_name;
      if (finalResponse) finalFileName = finalResponse.file_name;
      await Promise.all([
        initialResponse?.presigned_url
          ? fetch(initialResponse.presigned_url, {
            method: "PUT",
            body: initialFile,
            headers: { "Content-Type": initialFile.type },
          })
          : null,
        finalResponse?.presigned_url
          ? fetch(finalResponse.presigned_url, {
            method: "PUT",
            body: finalFile,
            headers: { "Content-Type": finalFile.type },
          })
          : null,
      ]);
      const statusPayload = {
        [updatedStatus]: {
          closed_on: closedDate,
          closed_by: closedBy,
          initial_response_to_db: initialFileName,
          final_response_to_db: finalFileName,
          comment: comment || "",
        },
      };

      const payload = { status: statusPayload };

      const response = await updateBreachStatusChange(
        breachDetails?.breach_id,
        payload
      );

      if (response?.status_code === 200) {
        const res = await getBreachData(id); // Refresh the breach list
        setBreachDetails(res?.data || {});
        setOpenModal(false); // Close modal on success
        showSuccessToast(response?.message);
      }
    } catch (err) {
      showErrorToast("Failed to update breach status.");
    } finally {
      setLoading(false);
      setErrors({});
    }
  };

  const handleReAssignModalOpen = () => {
    setClosedBy(breachDetails?.assignee);
    const matchingUser = userData.find(
      (user) => user.user_id === breachDetails.assignee
    );
    setAssignee(matchingUser ? matchingUser.user_id : "");
    setReAssignModalOpen(true);
  };

  const isSaveDisabled = loading || assignee === breachDetails?.assignee;

  const handleAssigneeSubmit = async () => {
    setLoading(true);
    if (!assignee) {
      return;
    }
    const payload = {
      assignee: assignee,
    };
    try {
      const response = await assignBreachUser(
        breachDetails?.breach_id,
        payload
      );
      if (response.status_code === 200) {
        const res = await getBreachData(id);
        setBreachDetails(res?.data || {});
        showSuccessToast(response.message);
        setReAssignModalOpen(false);
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 3 }}>
        <CommonHeader
          title="Data Breach Details"
          subTitle="Review breach details on this page."
        />
        <Box gap={2} display="flex">
          <ButtonCommon
            onClick={() => {
              setOpenModal(true);
              setUpdatedStatus(Object.keys(breachDetails?.status || {})[0]);
              setClosedBy(breachDetails?.assignee);
            }}
            disabled={
              latestStatusKey === "closed" ||
              Object.keys(breachDetails || {}).length === 0
            }
          >
            Change Status
          </ButtonCommon>
          {currentUser && currentUser.zoneinfo !== "Org_user" && (
            <ButtonCommon
              onClick={handleReAssignModalOpen}
              disabled={
                latestStatusKey === "closed" ||
                Object.keys(breachDetails || {}).length === 0
              }
            >
              Reassign
            </ButtonCommon>
          )}

          <ButtonCommon
            onClick={() => setOpen(true)}
            disabled={
              latestStatusKey === "closed" ||
              Object.keys(breachDetails || {}).length === 0
            }
          >
            Edit
          </ButtonCommon>
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <CustomBreadcrumb
          items={breadcrumbItems}
          onBackClick={() => navigate("/data-breach")}
        />
      </Box>

      {loading ? (
        <Loader />
      ) : (
        Object.keys(breachDetails || {}).length > 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: "#F9FAFB", height: "100%" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="#525455"
                  fontWeight="bold"
                >
                  Data Breach Information
                </Typography>
                <Divider />
                {breachInfoFields.map(({ key, label, format }, index) => {
                  const isLastItem = index === breachInfoFields.length - 1; // Check if it's the last item
                  return (
                    <Box key={key} sx={{ mb: 1, mt: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textTransform: "capitalize", display: "block" }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: "break-word", whiteSpace: "normal" }}
                      >
                        {format
                          ? format(breachDetails[key], breachDetails)
                          : breachDetails[key] !== undefined &&
                            breachDetails[key] !== null
                            ? breachDetails[key]
                            : "Not Available"}
                      </Typography>
                      {!isLastItem && <Divider sx={{ mt: 2 }} />}{" "}
                      {/* Hide divider for the last item */}
                    </Box>
                  );
                })}
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <CommonTabs
                value={tabValue}
                handleChange={(_, newValue) => setTabValue(newValue)}
                tabs={tabs}
              />
            </Grid>
          </Grid>
        )
      )}
      <CommonDrawer
        open={open}
        onClose={() => setOpen(false)}
        title="Edit Breach Details"
        subTitle="Update the breach information as necessary."
        primaryButton={{
          label: "Update",
          onClick: handleUpdate,
          sx: { px: 4 },
          disabled: !isFormChanged || loading, // Disable button if no changes
        }}
        drawerWidth={500}
      >
        {/* Summary */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Summary
        </Typography>
        <TextField
          fullWidth
          size="small"
          name="summary"
          value={formData.summary}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          error={!!errors.summary}
          helperText={errors.summary}
        />

        {/* Detailed Description */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Detailed Description
        </Typography>
        <FormHelperText
          style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
        >
          A description of the breach, including its nature, extent, and the
          timing and location of its occurrence.
        </FormHelperText>
        <TextareaAutosize
          minRows={3}
          name="detailed_description"
          value={formData.detailed_description}
          onChange={handleInputChange}
          style={{ width: "100%", padding: 8, fontSize: 14, marginBottom: 16 }}
        />
        {errors.detailed_description && (
          <p style={{ color: "red", fontSize: "12px", margin: 0 }}>
            {errors.detailed_description}
          </p>
        )}

        {/* Location of Breach */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Location of Breach
        </Typography>
        <TextField
          fullWidth
          size="small"
          name="location_of_breach"
          value={formData.location_of_breach}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          error={!!errors.location_of_breach}
          helperText={errors.location_of_breach}
        />

        {/* File Upload */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Attachment
        </Typography>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadFile />}
          fullWidth
          sx={{ mt: 1 }}
        >
          {formData.attachment instanceof File
            ? formData.attachment.name // If a new file is selected, show file name
            : formData.attachment || "Choose File"}
          <input
            type="file"
            accept=".pdf,.jpeg,.jpg"
            onChange={handleCreateFileChange}
            hidden
          />
        </Button>
        <Typography
          variant="caption"
          sx={{ mt: 1, color: "gray", display: "block" }}
        >
          * Accepted file formats: <strong>.jpg, .jpeg, .pdf</strong>
        </Typography>
        {errors.file && (
          <Typography color="error" variant="caption">
            {errors.file}
          </Typography>
        )}
        {fileError && (
          <Typography color="error" variant="caption" display="flex">
            {fileError}
          </Typography>
        )}

        {/* Impact */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Impact
        </Typography>
        <FormHelperText
          style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
        >
          The consequences relevant to his/her, that are likely to arise from
          the breach.
        </FormHelperText>
        <TextField
          fullWidth
          size="small"
          name="impact"
          value={formData.impact}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          error={!!errors.impact}
          helperText={errors.impact}
        />

        {/* Mitigations */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Mitigations
        </Typography>
        <FormHelperText
          style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
        >
          The measures implemented and being implemented by the Data Fiduciary,
          if any, to mitigate risk.
        </FormHelperText>
        <TextField
          fullWidth
          size="small"
          name="mitigations"
          value={formData.mitigations}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          error={!!errors.mitigations}
          helperText={errors.mitigations}
        />

        {/* Safety Measures */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Safety Measures for Data Principal
        </Typography>
        <FormHelperText
          style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
        >
          The safety measures that he/she may take to protect his/her interests.
        </FormHelperText>
        <TextField
          fullWidth
          size="small"
          name="safety_measures_for_dp"
          value={formData.safety_measures_for_dp}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          error={!!errors.safety_measures_for_dp}
          helperText={errors.safety_measures_for_dp}
        />

        {/* Root Cause */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Root Cause
        </Typography>
        <FormHelperText
          style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
        >
          The broad facts related to the events, circumstances and reasons
          leading to the breach.
        </FormHelperText>
        <TextField
          fullWidth
          size="small"
          name="root_cause"
          value={formData.root_cause}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          error={!!errors.root_cause}
          helperText={errors.root_cause}
        />

        {/* Responsible Person */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Responsible Person for Breach
        </Typography>
        <FormHelperText
          style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
        >
          Any findings regarding the person who caused the breach.
        </FormHelperText>
        <TextField
          fullWidth
          size="small"
          name="responsible_person"
          value={formData.responsible_person}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          error={!!errors.responsible_person}
          helperText={errors.responsible_person}
        />

        {/* Remedial Measures */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Remedial Measures
        </Typography>
        <FormHelperText
          style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
        >
          Remedial measures taken to prevent recurrence of such breach.
        </FormHelperText>
        <TextField
          fullWidth
          size="small"
          name="remedial_measures"
          value={formData.remedial_measures}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
          error={!!errors.remedial_measures}
          helperText={errors.remedial_measures}
        />

        {/* Intimations to DPs */}
        <Typography variant="body" gutterBottom fontWeight={500}>
          Intimations to DPs
        </Typography>
        <FormHelperText
          style={{ fontSize: "14px", color: "#666", marginBottom: "5px" }}
        >
          A report regarding the intimations given to affected Data Principals.
        </FormHelperText>
        <TextareaAutosize
          minRows={3}
          name="intimations_to_dps"
          value={formData.intimations_to_dps}
          onChange={handleInputChange}
          style={{ width: "100%", padding: 8, fontSize: 14, marginBottom: 16 }}
        />
        {errors.intimations_to_dps && (
          <p style={{ color: "red", fontSize: "12px", margin: 0 }}>
            {errors.intimations_to_dps}
          </p>
        )}
      </CommonDrawer>

      {/* Status Change Modal */}
      <Modal open={openModal}>
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              Update Breach Status
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseOutlined />
            </IconButton>
          </Box>

          {/* Breach ID */}
          <Typography variant="body1">
            Breach ID <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            value={breachDetails?.breach_id || ""}
            disabled
            fullWidth
            sx={{ mt: 1 }}
            size="small"
            error={!!errors.breach_id}
            helperText={errors.breach_id}
          />

          {/* Status Dropdown */}
          <Typography variant="body1" sx={{ mt: 2 }}>
            Status  <span style={{ color: "red" }}>*</span>
          </Typography>
          <Select
            value={updatedStatus}
            onChange={handleStatusChange}
            fullWidth
            size="small"
            error={!!errors.status}
          >
            {[
              { value: "new", label: "New" },
              // { value: "dp_informed", label: "DP Informed" },
              // { value: "dpb_informed", label: "DPB Informed" },
              // { value: "details_sent_to_dpb", label: "Details Sent to DPB" },
              { value: "closed", label: "Closed" },
            ].map((option, index, array) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={
                  option.value === updatedStatus || // 🔥 Disable currently selected
                  array.findIndex((opt) => opt.value === updatedStatus) > index // 🔥 Disable previous options
                }
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>

          {errors.status && (
            <Typography color="error" variant="caption">
              {errors.status}
            </Typography>
          )}

          {updatedStatus === "closed" && (
            <>
              {/* Closed Date */}
              <Typography variant="body1" sx={{ mt: 2 }}>
                Closed Date  <span style={{ color: "red" }}>*</span>
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  size="small"
                  value={closedDate}
                  onChange={setClosedDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  sx={{ width: "100%" }}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth sx={{ mt: 1 }} />
                  )}
                  slotProps={{
                    textField: {
                      readOnly: true, // Prevents manual input
                    },
                  }}
                />
              </LocalizationProvider>
              {errors.closedDate && (
                <Typography color="error" variant="caption">
                  {errors.closedDate}
                </Typography>
              )}

              {/* Closed By Dropdown */}
              <Typography variant="body1" sx={{ mt: 2 }}>
                Closed By  <span style={{ color: "red" }}>*</span>
              </Typography>
              {currentUser?.zoneinfo === "Billing_Admin" ? (
                <Select
                  value={closedBy}
                  onChange={(event) => {
                    setClosedBy(event.target.value);
                  }}
                  fullWidth
                  error={!!errors.closedBy}
                  sx={{ mt: 1 }}
                  size="small"
                >
                  {(() => {
                    const billingAdmin = (
                      Array.isArray(userData) ? userData : []
                    ).find(
                      (user) =>
                        user.email?.toLowerCase() ===
                        currentUser?.email?.toLowerCase()
                    );

                    const assignee = (
                      Array.isArray(userData) ? userData : []
                    ).find((user) => user.user_id === breachDetails?.assignee);

                    const uniqueUsers = [billingAdmin, assignee].filter(
                      (user, index, self) =>
                        user &&
                        self.findIndex((u) => u?.user_id === user?.user_id) ===
                        index
                    );

                    return (Array.isArray(uniqueUsers) ? uniqueUsers : []).map(
                      (user) => (
                        <MenuItem key={user.user_id} value={user.user_id}>
                          {user.full_name}
                        </MenuItem>
                      )
                    );
                  })()}
                </Select>
              ) : (
                <TextField
                  value={breachDetails?.assignee_name}
                  fullWidth
                  sx={{ mt: 1 }}
                  disabled
                />
              )}

              {errors.closedBy && (
                <Typography color="error" sx={{ mt: 1 }}>
                  {errors.closedBy}
                </Typography>
              )}

              {/* File Upload */}
              <Typography variant="body1" sx={{ mt: 2 }}>
                Initial Response to DPB  <span style={{ color: "red" }}>*</span>
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadFile />}
                fullWidth
                sx={{ mt: 1 }}
              >
                {initialFile ? initialFile.name : "Choose File"}
                <input
                  type="file"
                  accept=".pdf,.jpeg,.jpg"
                  onChange={(e) => handleFileChange(e, "initial")}
                  hidden
                />
              </Button>
              <Typography
                variant="caption"
                sx={{ mt: 1, color: "gray", display: "block" }}
              >
                * Accepted file formats: <strong>.jpg, .jpeg, .pdf</strong>
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "gray", display: "block" }}
              >
                * Response to DPB immediately after breach.
              </Typography>
              {errors.file && (
                <Typography color="error" variant="caption">
                  {errors.file}
                </Typography>
              )}
              {fileError && (
                <Typography color="error" variant="caption">
                  {fileError}
                </Typography>
              )}
              {/* File Upload */}
              <Typography variant="body1" sx={{ mt: 2 }}>
                Final Response to DPB  <span style={{ color: "red" }}>*</span>
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadFile />}
                fullWidth
                sx={{ mt: 1 }}
              >
                {finalFile ? finalFile.name : "Choose File"}
                <input
                  type="file"
                  accept=".pdf,.jpeg,.jpg"
                  onChange={(e) => handleFileChange(e, "final")}
                  hidden
                />
              </Button>
              <Typography
                variant="caption"
                sx={{ mt: 1, color: "gray", display: "block" }}
              >
                * Accepted file formats: <strong>.jpg, .jpeg, .pdf</strong>
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "gray", display: "block" }}
              >
                * Detailed response sent to DPB within 72-hours of breach.
              </Typography>
              {errors.file && (
                <Typography color="error" variant="caption">
                  {errors.file}
                </Typography>
              )}
              {fileError && (
                <Typography color="error" variant="caption">
                  {fileError}
                </Typography>
              )}
            </>
          )}

          {/* Comment */}
          <Typography variant="body1" sx={{ mt: 2 }}>
            Comment
          </Typography>
          <TextField
            label="Comment"
            multiline
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <ButtonCommon
              onClick={handleSubmit}
              disabled={
                loading ||
                updatedStatus === Object.keys(breachDetails.status || {}).pop()
              }
            >
              {loading ? <CircularProgress size={20} /> : "Save"}
            </ButtonCommon>
            <ButtonCommon onClick={handleCloseModal}>Cancel</ButtonCommon>
          </Box>
        </Box>
      </Modal>

      <Modal open={reAssignModalOpen}>
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              Choose Assignee
            </Typography>
            <IconButton onClick={() => setReAssignModalOpen(false)}>
              <CloseOutlined />
            </IconButton>
          </Box>

          <Box
            sx={{
              borderRadius: "8px",
              mt: 2,
            }}
          >
            <InputLabel
              name="breach_id"
              sx={{ display: "block", marginTop: 1 }}
            >
              Breach ID
            </InputLabel>
            <TextField
              value={breachDetails?.breach_id || ""}
              disabled
              fullWidth
              sx={{ marginTop: 1, mb: 1 }}
              size="small"
            />

            {/* Closed By Dropdown */}
            <InputLabel
              name="Assignee"
              sx={{ display: "block", marginTop: 2 }}
              required
            >
              Assignee
            </InputLabel>
            <Select
              value={assignee || ""} // Ensuring controlled value
              onChange={(event) => setAssignee(event.target.value)}
              fullWidth
              sx={{ marginTop: 1 }}
              size="small"
            >
              {userData.map((user) => (
                <MenuItem key={user.user_id} value={user.user_id}>
                  {user.full_name}
                </MenuItem>
              ))}
            </Select>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, marginTop: 2 }}>
              <ButtonCommon
                onClick={handleAssigneeSubmit}
                disabled={isSaveDisabled}
              >
                {loading ? <CircularProgress size={20} /> : "Save"}
              </ButtonCommon>
              <ButtonCommon onClick={() => setReAssignModalOpen(false)}>
                Cancel
              </ButtonCommon>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DataBreachDetails;
